import { useState } from "react";
import { message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { getData } from "../../../../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../../../../api/ajax-endpoints";
import { AllRegisteredBusinessSteps } from "../..";
import styles from "../../RegisteredBusiness.module.scss";
import { ICorporateCustomer } from "../../../../hooks/useCorporateCustomers";
import { errorHandler } from "../../../../../../../helpers/errorHandler";

interface ICompleteOnboardingErrors {
  code: string;
  errors: string[];
}

interface ICompleteOnboarding {
  setCurrentStep: (step: number) => void;
  onComplete: () => void;
  resumeCustomerData: ICorporateCustomer;
  formData: any;
}

const CompleteOnboarding: React.FC<ICompleteOnboarding> = ({
  setCurrentStep,
  onComplete,
  resumeCustomerData,
  formData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [completeOnboardingErrors, setCompleteOnboardingErrors] = useState<
    ICompleteOnboardingErrors[] | undefined
  >();

  const RegisteredBusinessSteps = [
    {
      name: "Business Information",
      code: "BIE",
    },
    {
      name: "Business Owner Information",
      code: "BOIE",
    },
    {
      name: "Business Documents",
      code: "BDE",
    },
  ];

  function handleNavigation(stepCode: string) {
    switch (stepCode) {
      case "BIE":
        return setCurrentStep(AllRegisteredBusinessSteps.BUSINESS_INFORMATION);
      case "BOIE":
        return setCurrentStep(
          AllRegisteredBusinessSteps.BUSINESS_OWNERS_INFORMATION
        );
      case "BDE":
        return setCurrentStep(AllRegisteredBusinessSteps.BUSINESS_DOCUMENTS);
      default:
        return;
    }
  }

  const verifyOnboarding = async () => {
    try {
      setIsLoading(true);

      const response = await getData(
        `${ajaxEndpoints.COMPLETE_ONBOARDING}?customerId=${formData.funder_id || resumeCustomerData.customer_id}`
      );

      message.success(
        response?.data?.message || "Business onboarding completed successfully!"
      );
      onComplete();
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to complete onboarding");

      const errors = error?.response?.data?.data;
      setCompleteOnboardingErrors(errors);
    } finally {
      setIsLoading(false);
    }
  };

  const checkError = (step: string) => {
    if (completeOnboardingErrors)
      return completeOnboardingErrors.some((error) => error.code === step);
  };

  return (
    <div className={styles.CompleteOnboarding}>
      <div>
        {RegisteredBusinessSteps?.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              marginBottom: "1rem",
            }}
            onClick={() => {
              if (checkError(item.code)) {
                handleNavigation(item.code);
              }
            }}
          >
            {!checkError(item.code) ? (
              <div style={{ fontSize: "1.5rem", color: "#25ad25" }}>
                <CheckCircleOutlined />
              </div>
            ) : (
              <div style={{ fontSize: "1.5rem", color: "#e31a1a" }}>
                <CloseCircleOutlined />
              </div>
            )}
            <div className={styles.StepContainer}>
              <div className={styles.Step}>
                <p style={{ margin: 0 }}>{item.name}</p>
              </div>
              <div>
                {checkError(item.code) &&
                  completeOnboardingErrors &&
                  completeOnboardingErrors
                    .find((error) => error.code === item.code)
                    ?.errors.map((error, errorIndex) => (
                      <p
                        key={errorIndex}
                        style={{
                          fontSize: "0.75rem",
                          margin: "0.25rem 0",
                          color: "#e31a1a",
                        }}
                      >
                        {errorIndex + 1}) {error}.
                      </p>
                    ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.ActionButtonContainer}>
        <button
          type="button"
          onClick={verifyOnboarding}
          className="btn advancly-btn btn-lg"
          disabled={isLoading}
          style={{ flex: 1 }}
        >
          {isLoading ? "Completing..." : "Complete Onboarding"}
        </button>
      </div>
    </div>
  );
};

export default CompleteOnboarding;
